import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Switch } from "./ui/switch";
import { Loader2, RotateCcw, Save, Download } from "lucide-react";
import { toast } from "sonner";
import { generateServicePDF, DEFAULT_PDF_DESIGN, mergePdfDesign } from "../lib/pdfGenerator";

const SAMPLE_JOB = {
  job_number: "SP-ORNEK-001",
  customer_name: "Ahmet Yılmaz",
  customer_phone: "0532 000 00 00",
  customer_address: "Örnek Mah. Deneme Sok. No:5 Çankaya / Ankara",
  device_brand_name: "Vaillant",
  device_model: "ecoTEC Plus 24",
  device_type: "Kombi",
  device_serial: "SN-123456",
  fault_description: "Kombi su basıncı düşüyor, ısıtmıyor.",
  work_done: "Genleşme tankı kontrol edildi, basınç ayarlandı, plakalı eşanjör temizlendi.",
  pdf_note: "Cihaz 6 ay garantilidir.",
  labor_fee: 450,
  parts_used: [
    { name: "Genleşme tankı", qty: 1, unit_price: 650, total: 650 },
    { name: "Su basınç sensörü", qty: 1, unit_price: 320, total: 320 },
  ],
  assigned_technician_name: "Örnek Usta",
  created_at: new Date().toISOString(),
  completed_at: new Date().toISOString(),
};

const TOGGLES = [
  ["show_brand_logo", "Cihaz marka logosu"],
  ["show_parts", "Parça tablosu"],
  ["show_job_note", "Servis notu"],
  ["show_default_note", "Önemli notlar (Ayarlar'daki varsayılan not)"],
  ["show_signatures", "İmza alanları"],
  ["show_footer_logo", "Alt kısım ServisPlus logosu"],
  ["show_dates", "Oluşturma / tamamlanma tarihi"],
];
const LABELS = [
  ["title", "Başlık"],
  ["customer_label", "Müşteri bölümü"],
  ["device_label", "Cihaz bölümü"],
  ["fault_label", "Arıza bölümü"],
  ["work_label", "Yapılan işlem bölümü"],
  ["parts_label", "Parça bölümü"],
  ["notes_label", "Notlar bölümü"],
  ["customer_sig_label", "Müşteri imzası"],
  ["tech_sig_label", "Teknisyen imzası"],
  ["footer_text", "Alt bilgi metni (isteğe bağlı)"],
];

export default function PdfDesignDialog({ open, onOpenChange }) {
  const [design, setDesign] = useState(DEFAULT_PDF_DESIGN);
  const [url, setUrl] = useState("");
  const [busy, setBusy] = useState(false);
  const [saving, setSaving] = useState(false);
  const timer = useRef(null);
  const loaded = useRef(false);

  useEffect(() => {
    if (!open) return;
    loaded.current = false;
    api.get("/settings").then((r) => setDesign(mergePdfDesign(r.data?.pdf_design))).catch(() => {}).finally(() => { loaded.current = true; setDesign((d) => ({ ...d })); });
  }, [open]);

  useEffect(() => {
    if (!open || !loaded.current) return;
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setBusy(true);
      try {
        const blob = await generateServicePDF(SAMPLE_JOB, { design, preview: true });
        setUrl((old) => { if (old) URL.revokeObjectURL(old); return URL.createObjectURL(blob); });
      } catch (e) { /* önizleme hatası sessiz */ }
      finally { setBusy(false); }
    }, 450);
    return () => clearTimeout(timer.current);
  }, [design, open]);

  useEffect(() => () => { if (url) URL.revokeObjectURL(url); }, []); // eslint-disable-line

  const set = (k, v) => setDesign((d) => ({ ...d, [k]: v }));
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", { pdf_design: design });
      toast.success("PDF tasarımı kaydedildi — tüm servis fişlerinde kullanılacak");
      onOpenChange(false);
    } catch (e) { toast.error(e.response?.data?.detail || "Kaydedilemedi"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-6xl w-[96vw] max-h-[94vh] overflow-hidden p-0 gap-0" data-testid="pdf-design-dialog">
        <DialogHeader className="px-6 py-4 border-b border-slate-200">
          <DialogTitle className="font-display text-xl">PDF Tasarımı — Servis Fişi</DialogTitle>
          <DialogDescription>Değişiklikler sağdaki örnek fişte anında görünür. Kaydedince tüm servis fişlerine (usta ekranı dahil) uygulanır.</DialogDescription>
        </DialogHeader>
        <div className="grid lg:grid-cols-[360px_1fr] h-[72vh]">
          <div className="overflow-y-auto p-5 space-y-5 border-r border-slate-200">
            <div className="space-y-2">
              <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Görünüm</div>
              <div className="flex items-center gap-3">
                <Label className="w-28 text-sm">Başlık rengi</Label>
                <input type="color" value={design.title_color} onChange={(e) => set("title_color", e.target.value)}
                  className="h-9 w-14 rounded border border-slate-300 cursor-pointer" data-testid="pdf-title-color" />
                <span className="text-xs text-slate-500">{design.title_color}</span>
              </div>
            </div>
            <div className="space-y-2.5">
              <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Gösterilecek alanlar</div>
              {TOGGLES.map(([k, l]) => (
                <div key={k} className="flex items-center justify-between gap-3">
                  <Label className="text-sm font-normal">{l}</Label>
                  <Switch checked={!!design[k]} onCheckedChange={(v) => set(k, v)} data-testid={`pdf-toggle-${k}`} />
                </div>
              ))}
            </div>
            <div className="space-y-2.5">
              <div className="text-xs uppercase tracking-widest font-bold text-slate-500">Başlıklar ve metinler</div>
              {LABELS.map(([k, l]) => (
                <div key={k}>
                  <Label className="text-xs text-slate-500">{l}</Label>
                  <Input value={design[k] ?? ""} onChange={(e) => set(k, e.target.value)} className="h-9 mt-1" data-testid={`pdf-input-${k}`} />
                </div>
              ))}
            </div>
          </div>
          <div className="bg-slate-100 relative">
            {busy && <div className="absolute top-3 right-4 z-10 text-xs bg-white border border-slate-200 rounded px-2 py-1 flex items-center gap-1.5"><Loader2 className="h-3 w-3 animate-spin" /> Güncelleniyor</div>}
            {url ? <iframe title="PDF önizleme" src={url} className="w-full h-full border-0" data-testid="pdf-preview-frame" />
              : <div className="h-full grid place-items-center text-slate-500 text-sm"><Loader2 className="h-5 w-5 animate-spin" /></div>}
          </div>
        </div>
        <div className="px-6 py-3 border-t border-slate-200 flex items-center justify-between gap-2 bg-white">
          <Button variant="outline" onClick={() => setDesign(DEFAULT_PDF_DESIGN)} data-testid="pdf-reset">
            <RotateCcw className="h-4 w-4 mr-1.5" /> Varsayılana dön
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={async () => { const b = await generateServicePDF(SAMPLE_JOB, { design, preview: true }); const a = document.createElement("a"); a.href = URL.createObjectURL(b); a.download = "ornek-servis-fisi.pdf"; a.click(); }}>
              <Download className="h-4 w-4 mr-1.5" /> Örneği indir
            </Button>
            <Button onClick={save} disabled={saving} className="bg-blue-600 hover:bg-blue-700" data-testid="pdf-design-save">
              {saving ? <Loader2 className="h-4 w-4 animate-spin mr-1.5" /> : <Save className="h-4 w-4 mr-1.5" />} Kaydet
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
